/**
 * Categorical color palette
 * For 7 or fewer categories: Uses Paul Tol's muted palette (excluding gray)
 * For more than 7 categories: Uses ColorBrewer Stepped Sequential schemes
 */

/**
 * Get a color from the categorical palette
 * @param {number} index - The zero-based index of the category
 * @param {number} totalCount - Total number of categories
 * @returns {string} - RGB color string
 */
export function getCategoricalColor(index, totalCount = 0) {
  // Paul Tol's muted color palette (excluding gray #DDDDDD)
  // https://personal.sron.nl/~pault/
  const tolMutedPalette = [
    "rgb(136, 204, 238)", // Cyan
    "rgb(204, 102, 119)", // Rose
    "rgb(221, 204, 119)", // Sand
    "rgb(17, 119, 51)", // Green
    "rgb(153, 153, 51)", // Olive
    "rgb(68, 170, 153)", // Teal
    "rgb(170, 68, 153)", // Purple
  ];

  // For 7 or fewer categories, use Paul Tol's muted palette
  if (totalCount <= 7) {
    return tolMutedPalette[index % tolMutedPalette.length];
  }

  // For more than 7 categories, use ColorBrewer stepped sequential palettes
  // Array of colorblind-friendly sequential palettes (5 most saturated colors from each)
  const steppedSequentialPalettes = [
    // Blues (most saturated to least saturated)
    [
      "rgb(8, 48, 107)",
      "rgb(33, 113, 181)",
      "rgb(107, 174, 214)",
      "rgb(189, 215, 231)",
      "rgb(239, 243, 255)",
    ],
    // Greens (most saturated to least saturated)
    [
      "rgb(0, 68, 27)",
      "rgb(35, 139, 69)",
      "rgb(116, 196, 118)",
      "rgb(186, 228, 179)",
      "rgb(237, 248, 233)",
    ],
    // Oranges (most saturated to least saturated)
    [
      "rgb(127, 39, 4)",
      "rgb(217, 71, 1)",
      "rgb(253, 141, 60)",
      "rgb(253, 190, 133)",
      "rgb(254, 237, 222)",
    ],
    // Purples (most saturated to least saturated)
    [
      "rgb(63, 0, 125)",
      "rgb(106, 81, 163)",
      "rgb(158, 154, 200)",
      "rgb(203, 201, 226)",
      "rgb(242, 240, 247)",
    ],
    // Reds (most saturated to least saturated)
    [
      "rgb(103, 0, 13)",
      "rgb(203, 24, 29)",
      "rgb(251, 106, 74)",
      "rgb(252, 174, 145)",
      "rgb(254, 229, 217)",
    ],
  ];

  // Calculate which palette and which color within that palette
  const paletteIndex = Math.floor(index / 5) % steppedSequentialPalettes.length;
  const colorIndex = index % 5;

  return steppedSequentialPalettes[paletteIndex][colorIndex];
}
